import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { emailTemplateService } from '../../services/emailTemplateService';
import { SMTPConfig } from '../../types/training';
import { EmailTemplateEditor } from '../../components/EmailTemplateEditor';
import { EmailTemplate } from '../../types/emailTemplate';

export const SMTPConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<SMTPConfig>({
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: 'WMPG Training Hub',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSaveWelcomeTemplate = async (template: Omit<EmailTemplate, 'type' | 'availableVariables'>) => {
    await emailTemplateService.updateEmailTemplate('welcome', template);
  };

  const handleSaveTrainingAssignmentTemplate = async (template: Omit<EmailTemplate, 'type' | 'availableVariables'>) => {
    await emailTemplateService.updateEmailTemplate('training_assignment', template);
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSMTPConfig();
      setConfig(data);
    } catch (err: any) {
      // Config might not exist yet, that's okay
      if (!err.message?.includes('404')) {
        setError(err.message || 'Failed to load SMTP configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setSaving(true);
      await adminService.updateSMTPConfig(config);
      setSuccess('SMTP configuration saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save SMTP configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError(null);
    setTestResult(null);

    try {
      setTesting(true);
      const result = await adminService.testSMTPConnection(config);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Failed to test SMTP connection',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin')}
        className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Site Configuration</h2>
        <p className="mt-1 text-sm text-gray-600">
          Configure email settings and templates for sending notifications to practitioners
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {testResult && (
        <div
          className={`border rounded-lg p-4 ${
            testResult.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start">
            {testResult.success ? (
              <svg
                className="h-5 w-5 text-green-400 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-red-400 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            <div className="ml-3">
              <h3
                className={`text-sm font-medium ${
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {testResult.success ? 'Connection Successful' : 'Connection Failed'}
              </h3>
              <p
                className={`text-sm mt-1 ${
                  testResult.success ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="host" className="block text-sm font-medium text-gray-700">
              SMTP Host
            </label>
            <input
              type="text"
              id="host"
              required
              value={config.host}
              onChange={(e) => setConfig({ ...config, host: e.target.value })}
              placeholder="smtp.example.com"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="port" className="block text-sm font-medium text-gray-700">
              Port
            </label>
            <input
              type="number"
              id="port"
              required
              value={config.port}
              onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Common ports: 25, 465 (SSL), 587 (TLS)</p>
          </div>

          <div>
            <label htmlFor="secure" className="block text-sm font-medium text-gray-700">
              Encryption
            </label>
            <select
              id="secure"
              value={config.secure ? 'true' : 'false'}
              onChange={(e) => setConfig({ ...config, secure: e.target.value === 'true' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="false">TLS (STARTTLS)</option>
              <option value="true">SSL/TLS</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Use SSL/TLS for port 465, TLS for other ports
            </p>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Username
            </label>
            <input
              type="text"
              id="username"
              required
              value={config.username}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              type="password"
              id="password"
              required
              value={config.password}
              onChange={(e) => setConfig({ ...config, password: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="fromEmail" className="block text-sm font-medium text-gray-700">
              From Email Address
            </label>
            <input
              type="email"
              id="fromEmail"
              required
              value={config.fromEmail}
              onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
              placeholder="noreply@example.com"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="fromName" className="block text-sm font-medium text-gray-700">
              From Name
            </label>
            <input
              type="text"
              id="fromName"
              required
              value={config.fromName}
              onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
              placeholder="WMPG Training Hub"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !config.host || !config.username || !config.password || !config.fromEmail}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {testing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Testing...
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Test Connection
              </>
            )}
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>

      {/* Email Template Sections */}
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Email Templates</h3>
          <p className="text-sm text-gray-600 mb-6">
            Customize the email templates sent to practitioners for various notifications
          </p>
        </div>

        {/* Welcome Email Template */}
        <EmailTemplateEditor
          templateType="welcome"
          onSave={handleSaveWelcomeTemplate}
        />

        {/* Training Assignment Email Template */}
        <EmailTemplateEditor
          templateType="training_assignment"
          onSave={handleSaveTrainingAssignmentTemplate}
        />
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Configuration Help</h3>
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">SMTP Configuration</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>For Gmail: Use smtp.gmail.com, port 587, and an app-specific password</li>
              <li>For Outlook: Use smtp-mail.outlook.com, port 587</li>
              <li>For SendGrid: Use smtp.sendgrid.net, port 587, username "apikey"</li>
              <li>Always test the connection before saving to ensure settings are correct</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Email Templates</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Use template variables like {`{{.PractitionerName}}`} to personalize emails</li>
              <li>Use link syntax [link site:URL text:TEXT] to create clickable links</li>
              <li>Preview templates before saving to see how they will appear</li>
              <li>Each template can be saved independently</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
